import React, { useState, useRef, useCallback, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows, Html, PresentationControls } from '@react-three/drei';
import * as THREE from 'three';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { VEHICLE_SPECS, CATEGORY_COLORS, formatPrice } from '../data/components';
import { SeatModel, ReclinerModel, SofaBedModel, FoldableSeatModel, PantryModel, FridgeModel, TrayTableModel, BBQModel, ToiletModel, TentModel, CanopyModel, UmbrellaModel, OutdoorSeatModel, CabinetModel, StorageModel, WardrobeModel, PartitionModel, SpeakerModel, TVModel, ControlPanelModel, ChargingDockModel, PlatformLiftModel, FallbackModel } from './ComponentModels';
import { X, Download, Share2, RotateCcw, Maximize2, Camera, Sun, Moon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CELL = 0.15;
const COLS = VEHICLE_SPECS.gridCols;
const ROWS = VEHICLE_SPECS.gridRows;

const MODEL_MAP = {
  'seat-captain': SeatModel,
  'seat-recliner': ReclinerModel,
  'seat-sofa': SofaBedModel,
  'seat-foldable': FoldableSeatModel,
  'util-pantry': PantryModel,
  'util-fridge': FridgeModel,
  'util-tray': TrayTableModel,
  'util-bbq': BBQModel,
  'util-toilet': ToiletModel,
  'life-tent': TentModel,
  'life-canopy': CanopyModel,
  'life-umbrella': UmbrellaModel,
  'life-outdoor': OutdoorSeatModel,
  'store-overhead': CabinetModel,
  'store-underseat': StorageModel,
  'store-wardrobe': WardrobeModel,
  'store-partition': PartitionModel,
  'elec-sound': SpeakerModel,
  'elec-tv': TVModel,
  'elec-control': ControlPanelModel,
  'elec-charger': ChargingDockModel,
  'elec-platform': PlatformLiftModel,
};

function getModelForComp(compId) {
  for (const [prefix, Model] of Object.entries(MODEL_MAP)) {
    if (compId.startsWith(prefix)) return Model;
  }
  return FallbackModel;
}

function RenderComponent({ pc, comp, customMaterials }) {
  const colors = CATEGORY_COLORS[comp.category] || CATEGORY_COLORS.Storage;
  const x = (pc.x + comp.grid_w / 2) * CELL - (COLS * CELL) / 2;
  const z = (pc.y + comp.grid_h / 2) * CELL - (ROWS * CELL) / 2;
  const matProps = customMaterials?.[pc.instance_id] || {};
  const ModelComponent = getModelForComp(comp.comp_id);
  const rotation = pc.rotation || 0;
  const rotationRad = (rotation * Math.PI) / 180;

  return (
    <group position={[x, 0, z]} rotation={[0, rotationRad, 0]}>
      <ModelComponent
        width={comp.grid_w}
        height={comp.grid_h}
        color={matProps.color || colors.fill}
        fabricColor={matProps.fabricColor}
        woodFinish={matProps.woodFinish}
      />
    </group>
  );
}

function VehicleFloorRender({ showGrid = false }) {
  const w = COLS * CELL;
  const h = ROWS * CELL;

  return (
    <group>
      {/* Main floor with better material */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]} receiveShadow>
        <planeGeometry args={[w + 0.2, h + 0.2]} />
        <meshStandardMaterial 
          color="#1a1a2e" 
          roughness={0.3} 
          metalness={0.1}
        />
      </mesh>

      {/* Vehicle body outline */}
      <mesh position={[0, 0.1, 0]}>
        <boxGeometry args={[w + 0.1, 0.2, h + 0.1]} />
        <meshStandardMaterial color="#0f172a" transparent opacity={0.3} />
      </mesh>

      {/* Side rails */}
      <mesh position={[-(w / 2) - 0.02, 0.05, 0]}>
        <boxGeometry args={[0.04, 0.15, h]} />
        <meshStandardMaterial color="#334155" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[(w / 2) + 0.02, 0.05, 0]}>
        <boxGeometry args={[0.04, 0.15, h]} />
        <meshStandardMaterial color="#334155" metalness={0.6} roughness={0.3} />
      </mesh>

      {/* Front and rear */}
      <mesh position={[0, 0.05, -(h / 2) - 0.02]}>
        <boxGeometry args={[w + 0.1, 0.15, 0.04]} />
        <meshStandardMaterial color="#1e293b" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[0, 0.05, (h / 2) + 0.02]}>
        <boxGeometry args={[w + 0.1, 0.15, 0.04]} />
        <meshStandardMaterial color="#1e293b" metalness={0.6} roughness={0.3} />
      </mesh>

      {/* Optional grid overlay */}
      {showGrid && (
        <>
          {Array.from({ length: COLS + 1 }).map((_, i) => (
            <mesh key={`gv${i}`} position={[i * CELL - w / 2, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
              <planeGeometry args={[0.002, h]} />
              <meshBasicMaterial color="#3b82f6" transparent opacity={0.2} />
            </mesh>
          ))}
          {Array.from({ length: ROWS + 1 }).map((_, i) => (
            <mesh key={`gh${i}`} position={[0, 0.002, i * CELL - h / 2]} rotation={[-Math.PI / 2, 0, 0]}>
              <planeGeometry args={[w, 0.002]} />
              <meshBasicMaterial color="#3b82f6" transparent opacity={0.2} />
            </mesh>
          ))}
        </>
      )}
    </group>
  );
}

export default function DesignRender({ 
  components, 
  placedComponents, 
  customMaterials = {},
  designName = "Custom Design",
  totalCost = 0,
  totalWeight = 0,
  onClose 
}) {
  const [lightMode, setLightMode] = useState('studio'); // studio, day, night
  const [showGrid, setShowGrid] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const canvasRef = useRef();

  const categoryStats = {};
  placedComponents.forEach(pc => {
    const comp = components.find(c => c.comp_id === pc.comp_id);
    if (comp) {
      if (!categoryStats[comp.category]) {
        categoryStats[comp.category] = { count: 0, cost: 0 };
      }
      categoryStats[comp.category].count++;
      categoryStats[comp.category].cost += comp.price;
    }
  });

  const handleDownload = useCallback(() => {
    // Trigger a screenshot download (uses html2canvas-like approach via Three.js)
    if (canvasRef.current) {
      const canvas = canvasRef.current.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = `${designName.replace(/\s+/g, '_')}_render.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    }
  }, [designName]);

  const handleShare = useCallback(() => {
    // Copy a shareable message
    const text = `Check out my ${designName} design - ${placedComponents.length} components, ${formatPrice(totalCost)} total!`;
    navigator.clipboard.writeText(text);
  }, [designName, placedComponents.length, totalCost]);

  const lightPresets = {
    studio: { intensity: 1, color: '#ffffff', envPreset: 'studio' },
    day: { intensity: 1.2, color: '#fff5e0', envPreset: 'sunset' },
    night: { intensity: 0.5, color: '#a0c4ff', envPreset: 'night' },
  };

  const currentLight = lightPresets[lightMode];

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="design-render-modal">
      {/* Header */}
      <header className="h-14 bg-slate-900/90 backdrop-blur border-b border-white/10 flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-4">
          <span className="font-chivo font-bold text-white text-lg">{designName}</span>
          <Badge className="bg-blue-600/20 text-blue-400 border-blue-600/30 text-xs">
            {placedComponents.length} components
          </Badge>
          <span className="text-slate-400 text-sm font-jetbrains">{formatPrice(totalCost)}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Light mode toggle */}
          <div className="flex items-center bg-slate-800 rounded-lg p-0.5">
            <button
              onClick={() => setLightMode('studio')}
              className={`px-3 py-1.5 rounded text-xs transition-all ${lightMode === 'studio' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="light-studio-btn"
            >
              Studio
            </button>
            <button
              onClick={() => setLightMode('day')}
              className={`px-3 py-1.5 rounded text-xs transition-all ${lightMode === 'day' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="light-day-btn"
            >
              <Sun className="w-3 h-3 inline mr-1" />Day
            </button>
            <button
              onClick={() => setLightMode('night')}
              className={`px-3 py-1.5 rounded text-xs transition-all ${lightMode === 'night' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="light-night-btn"
            >
              <Moon className="w-3 h-3 inline mr-1" />Night
            </button>
          </div>

          <div className="w-px h-6 bg-white/10" />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowGrid(!showGrid)}
            className={`text-slate-400 hover:text-white ${showGrid ? 'bg-slate-800' : ''}`}
            data-testid="toggle-grid-btn"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRotate(!autoRotate)}
            className={`text-slate-400 hover:text-white ${autoRotate ? 'bg-slate-800' : ''}`}
            data-testid="toggle-rotate-btn"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-white/10" />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            className="text-slate-400 hover:text-white gap-1.5"
            data-testid="download-render-btn"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="text-slate-400 hover:text-white gap-1.5"
            data-testid="share-render-btn"
          >
            <Share2 className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-white/10" />

          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            data-testid="close-render-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main 3D View */}
      <div ref={canvasRef} className="flex-1 relative">
        <Canvas
          camera={{ position: [2, 2.5, 2.5], fov: 40 }}
          shadows
          gl={{ 
            antialias: true, 
            preserveDrawingBuffer: true,
            toneMapping: THREE.ACESFilmicToneMapping,
            toneMappingExposure: 1.2
          }}
          dpr={[1, 2]}
          data-testid="render-canvas"
        >
          <color attach="background" args={[lightMode === 'night' ? '#0a0a0f' : '#0f1729']} />
          
          {/* Lighting based on mode */}
          <ambientLight intensity={currentLight.intensity * 0.4} color={currentLight.color} />
          <directionalLight 
            position={[5, 10, 5]} 
            intensity={currentLight.intensity} 
            color={currentLight.color}
            castShadow
            shadow-mapSize={[2048, 2048]}
          />
          <directionalLight position={[-3, 6, -3]} intensity={currentLight.intensity * 0.3} />
          <pointLight position={[0, 4, 0]} intensity={currentLight.intensity * 0.2} color="#3b82f6" />

          {/* Environment for reflections */}
          <Environment preset={currentLight.envPreset} background={false} />

          {/* Floor with shadows */}
          <ContactShadows 
            position={[0, -0.01, 0]} 
            opacity={0.5} 
            scale={10} 
            blur={2} 
            far={4}
          />

          <VehicleFloorRender showGrid={showGrid} />

          <Suspense fallback={null}>
            {placedComponents.map(pc => {
              const comp = components.find(c => c.comp_id === pc.comp_id);
              if (!comp) return null;
              return (
                <RenderComponent
                  key={pc.instance_id}
                  pc={pc}
                  comp={comp}
                  customMaterials={customMaterials}
                />
              );
            })}
          </Suspense>

          <OrbitControls
            autoRotate={autoRotate}
            autoRotateSpeed={0.5}
            minPolarAngle={0.2}
            maxPolarAngle={Math.PI / 2.1}
            minDistance={1}
            maxDistance={8}
            enablePan={true}
            target={[0, 0.2, 0]}
          />
        </Canvas>

        {/* Stats Overlay */}
        <div className="absolute bottom-6 left-6 glass-panel rounded-xl p-4 max-w-xs" data-testid="render-stats">
          <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3">Design Summary</h4>
          <div className="space-y-2">
            {Object.entries(categoryStats).map(([cat, stats]) => (
              <div key={cat} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: CATEGORY_COLORS[cat]?.fill || '#666' }}
                  />
                  <span className="text-xs text-slate-400">{cat}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{stats.count}x</span>
                  <span className="text-xs text-blue-400 font-mono">{formatPrice(stats.cost)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 mt-3 pt-3 flex justify-between">
            <span className="text-xs text-slate-400">Total</span>
            <span className="text-sm text-white font-semibold font-jetbrains">{formatPrice(totalCost)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-slate-500">Weight</span>
            <span className="text-xs text-slate-400 font-mono">{totalWeight}kg</span>
          </div>
        </div>

        {/* Watermark */}
        <div className="absolute bottom-6 right-6 flex items-center gap-2 text-slate-600 text-xs">
          <img src="/aa-logo.png" alt="AA" className="h-5 opacity-50" />
          <span className="font-chivo">Archana Automotives</span>
        </div>
      </div>
    </div>
  );
}
